/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * QR-decoding helpers for the recovery wizard (#246).
 *
 * Strategy: prefer the platform `BarcodeDetector` (hardware-accelerated,
 * shipped in Chromium) and fall back to the pure-JS decoder from the
 * zero-dependency `qr` package (paulmillr — same author family as the
 * @noble/@scure stack already in use) on browsers without the Shape
 * Detection API (Firefox, Safari).
 *
 * Consumers: `TdForgotPass` camera-scan and image-upload paths. Both feed
 * the decoded payload string into the existing paste-path validation, so
 * a scanned/uploaded QR goes through exactly the same schema checks as a
 * pasted backup text.
 */

import { decodeQR } from 'qr/decode.js';

/** Minimal structural typing for the Shape Detection API (not in lib.dom). */
interface DetectedBarcodeLike {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: ImageBitmapSource | ImageData): Promise<DetectedBarcodeLike[]>;
}
interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats(): Promise<string[]>;
}

/** @returns the platform BarcodeDetector constructor, or null if absent. */
const nativeDetectorCtor = (): BarcodeDetectorConstructor | null => {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  return typeof ctor === 'function' ? ctor : null;
};

/**
 * Decode a QR code from RGBA pixel data.
 *
 * Tries the native `BarcodeDetector` first; on absence, unsupported
 * `qr_code` format, error, or an empty result it falls through to the
 * pure-JS decoder.
 *
 * @param image - RGBA pixels containing (hopefully) one QR code
 * @returns the decoded payload string, or `null` when no QR code is found
 */
export async function decodeQrFromImageData(
  image: ImageData
): Promise<string | null> {
  const ctor = nativeDetectorCtor();
  if (ctor) {
    try {
      const formats = await ctor.getSupportedFormats();
      if (formats.includes('qr_code')) {
        const detected = await new ctor({ formats: ['qr_code'] }).detect(image);
        const rawValue = detected[0]?.rawValue;
        if (rawValue) return rawValue;
      }
    } catch {
      // Fall through to the pure-JS decoder.
    }
  }
  try {
    return decodeQR(image);
  } catch {
    return null;
  }
}

/**
 * Decode a QR code from an image file/blob (the upload path).
 *
 * The image is composited onto a white background before decoding: QR
 * renderers (including `wa-qr-code`) may paint dark modules on a
 * *transparent* background, which alpha-ignoring decoders read as a solid
 * black square.
 *
 * @param blob - an image file (PNG/JPEG/…) selected by the user
 * @returns the decoded payload string, or `null` when the blob is not a
 *   decodable image or contains no readable QR code
 */
export async function decodeQrFromBlob(blob: Blob): Promise<string | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null; // not a decodable image
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
    return await decodeQrFromImageData(
      ctx.getImageData(0, 0, canvas.width, canvas.height)
    );
  } finally {
    bitmap.close();
  }
}

/**
 * Grab the current frame from a playing `<video>` element and decode it
 * (the camera-scan path).
 *
 * @param video - a video element attached to a `getUserMedia` stream
 * @returns the decoded payload string, or `null` when the frame is not
 *   ready yet or contains no readable QR code (the common case while the
 *   user is still aiming the camera)
 */
export async function decodeQrFromVideoFrame(
  video: HTMLVideoElement
): Promise<string | null> {
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return null;
  if (video.videoWidth === 0 || video.videoHeight === 0) return null;
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0);
  return decodeQrFromImageData(
    ctx.getImageData(0, 0, canvas.width, canvas.height)
  );
}
